import React, { useCallback, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TimesheetFilters = ({ dateRange, onDateRangeChange, statusFilter, onStatusChange, isEmployer, onExportClick, isExporting = false, canExport = true }) => {
  const getFirstDayOfMonth = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000));
    return thirtyDaysAgo.toISOString().split('T')[0];
  };

  const [startDate, setStartDate] = useState(dateRange?.start || getFirstDayOfMonth());
  const [endDate, setEndDate] = useState(dateRange?.end || '');

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'draft', label: 'Draft' },
    { value: 'submitted', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const today = new Date().toISOString().split('T')[0];

  // Sync local state when props change
  React.useEffect(() => {
    if (dateRange) {
      setStartDate(dateRange.start || '');
      setEndDate(dateRange.end || '');
    }
  }, [dateRange]);

  const handleStartDateChange = useCallback((e) => {
    const newStartDate = e.target.value;
    setStartDate(newStartDate);
    
    // If end date is before start date, adjust it
    if (endDate && newStartDate > endDate) {
      setEndDate(newStartDate);
      if (onDateRangeChange) {
        onDateRangeChange({ start: newStartDate, end: newStartDate });
      }
    } else {
      if (onDateRangeChange) {
        onDateRangeChange({ start: newStartDate, end: endDate });
      }
    }
  }, [endDate, onDateRangeChange]);

  const handleEndDateChange = useCallback((e) => {
    const newEndDate = e.target.value;
    setEndDate(newEndDate);
    
    // If start date is after end date, adjust it
    if (startDate && newEndDate < startDate) {
      setStartDate(newEndDate);
      if (onDateRangeChange) {
        onDateRangeChange({ start: newEndDate, end: newEndDate });
      }
    } else {
      if (onDateRangeChange) {
        onDateRangeChange({ start: startDate, end: newEndDate });
      }
    }
  }, [startDate, onDateRangeChange]);

  const handleClearDates = useCallback(() => {
    setStartDate('');
    setEndDate('');
    if (onDateRangeChange) {
      onDateRangeChange({ start: null, end: null });
    }
  }, [onDateRangeChange]);

  const handleTodayClick = useCallback(() => {
    setStartDate(today);
    setEndDate(today);
    if (onDateRangeChange) {
      onDateRangeChange({ start: today, end: today });
    }
  }, [today, onDateRangeChange]);

  const handleThisMonthClick = useCallback(() => {
    const firstDay = new Date(today.split('-')[0], parseInt(today.split('-')[1]) - 1, 1)
      .toISOString().split('T')[0];
    setStartDate(firstDay);
    setEndDate(today);
    if (onDateRangeChange) {
      onDateRangeChange({ start: firstDay, end: today });
    }
  }, [today, onDateRangeChange]);

  const getFormattedDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getRangeDescription = () => {
    if (!startDate && !endDate) return 'All Time';
    if (startDate === endDate) return `${getFormattedDate(startDate)}`;
    if (startDate && endDate) return `${getFormattedDate(startDate)} - ${getFormattedDate(endDate)}`;
    if (startDate) return `From ${getFormattedDate(startDate)}`;
    return `Until ${getFormattedDate(endDate)}`;
  };

  return (
    <div className="bg-white rounded-lg border border-border p-6">
      <div className="flex items-center mb-4">
        <Icon name="Calendar" size={20} className="text-text-secondary mr-2" />
        <h2 className="text-lg font-semibold text-text-primary">Filters</h2>
      </div>
      
      <div className="space-y-4">
        {/* Date Range Section */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Date Range
          </label>
          <div className="space-y-2 mb-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-text-secondary mb-1">From</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  max={today}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Start date"
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">To</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  max={today}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="End date"
                />
              </div>
            </div>
            <div className="text-xs text-text-secondary px-3 py-2 bg-secondary-50 rounded-lg">
              {getRangeDescription()}
            </div>
          </div>

          {/* Quick Date Buttons */}
          <div className="flex gap-2 mb-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleTodayClick}
              className="flex-1"
            >
              Today
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleThisMonthClick}
              className="flex-1"
            >
              This Month
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearDates}
              iconName="X"
              title="Clear dates"
            />
          </div>
        </div>

        {/* Status Filter */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Status
          </label>
          <select
            value={statusFilter}
            onChange={(e) => onStatusChange(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {isEmployer && (
          <div className="pt-4 border-t border-border">
            <div className="text-xs text-text-secondary">
              <p className="mb-1">• Pending entries require your approval</p>
              <p className="mb-1">• Approved entries are final</p>
              <p>• You can reject entries with feedback</p>
            </div>
          </div>
        )}

        {/* Export Button */}
        {onExportClick && (
          <div className="mt-4 pt-4 border-t border-border">
            <button
              onClick={onExportClick}
              disabled={isExporting || !canExport}
              className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19m0 0l-7-7m7 7l7-7m0-5V5a2 2 0 00-2-2H5a2 2 0 00-2 2v9" />
              </svg>
              {isExporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const MemoizedTimesheetFilters = React.memo(TimesheetFilters, (prevProps, nextProps) => {
  return (
    prevProps.dateRange?.start === nextProps.dateRange?.start &&
    prevProps.dateRange?.end === nextProps.dateRange?.end &&
    prevProps.statusFilter === nextProps.statusFilter &&
    prevProps.onDateRangeChange === nextProps.onDateRangeChange &&
    prevProps.onStatusChange === nextProps.onStatusChange &&
    prevProps.isEmployer === nextProps.isEmployer &&
    prevProps.isExporting === nextProps.isExporting &&
    prevProps.canExport === nextProps.canExport
  );
});

export default MemoizedTimesheetFilters;
